import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';

import { apiService } from '../services/api';
import { apiErrorMessage } from '../utils/apiError';
import { useToast } from './ToastProvider';

interface OrderSummary {
  id: number;
  auftragsnummer: string;
  status: 'open' | 'closed';
  coil_count: number;
}

interface DeleteInternalOrderDialogProps {
  order: OrderSummary | null;
  onClose: () => void;
  onDeleted: (order: OrderSummary) => void;
}

/** Admin-only confirmation for deleting an internal order (Auftrag). */
export default function DeleteInternalOrderDialog({
  order,
  onClose,
  onDeleted,
}: DeleteInternalOrderDialogProps) {
  const { toastError, toastSuccess } = useToast();
  const [deleting, setDeleting] = useState(false);

  const handleConfirm = async () => {
    if (!order) return;
    setDeleting(true);
    try {
      await apiService.deleteInternalOrder(order.id);
      toastSuccess(`Auftrag ${order.auftragsnummer} gelöscht.`);
      onDeleted(order);
    } catch (err: any) {
      toastError(apiErrorMessage(err, 'Auftrag konnte nicht gelöscht werden.'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={order !== null} onClose={() => !deleting && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle>Auftrag löschen?</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5}>
          <Typography>
            Auftrag <strong>{order?.auftragsnummer}</strong> wird unwiderruflich gelöscht.
          </Typography>
          {order && order.coil_count > 0 && (
            <Typography variant="body2" color="text.secondary">
              {order.coil_count === 1
                ? 'Das zugeordnete Coil wird'
                : `Die ${order.coil_count} zugeordneten Coils werden`}{' '}
              vom Auftrag gelöst, bleiben aber in ihren Chargen erhalten.
            </Typography>
          )}
          {order?.status === 'closed' && (
            <Typography variant="body2" color="warning.main">
              Der Auftrag ist bereits abgeschlossen. Das dazugehörige Zeugnis kann danach nicht
              mehr erzeugt werden.
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={deleting}>
          Abbrechen
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={() => void handleConfirm()}
          disabled={deleting}
        >
          {deleting ? 'Wird gelöscht…' : 'Löschen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
